import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LeadStatusBadge, LEAD_STATUS_OPTIONS } from "@/components/TranscriptModal";
import { Search, RefreshCw, Users, CheckCircle2, Clock, Trophy, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { ExportButton } from "@/components/ExportButton";
import { resolveRange, isInRange, toInputDate } from "@/lib/dateRange";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

export default function LeadsPage() {
  const { leads, leadsLoading, leadsFetchedOnce, refreshLeads, patchLead } = useData();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [preset, setPreset] = useState("today");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  const [editLead, setEditLead] = useState(null);
  const [editForm, setEditForm] = useState({ notes: "", follow_up_date: "", status: "new" });
  const [editBusy, setEditBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const leadId = searchParams.get("lead");
    if (leadId && leads.length) {
      const found = leads.find((l) => l.id === leadId);
      if (found) {
        openEdit(found);
        const next = new URLSearchParams(searchParams);
        next.delete("lead");
        setSearchParams(next, { replace: true });
      }
    }
  }, [leads, searchParams, setSearchParams]);

  const load = () => refreshLeads({ silent: false }).catch(() => toast.error("Failed to refresh"));

  const patchLeadRemote = async (leadId, payload, successMsg) => {
    try {
      const { data } = await api.patch(`/leads/${leadId}`, payload);
      patchLead(leadId, data);
      if (successMsg) toast.success(successMsg);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
      throw err;
    }
  };

  const updateStatus = (leadId, status) => patchLeadRemote(leadId, { status }, "Status updated");
  const updateFollowUp = (leadId, date) => patchLeadRemote(leadId, { follow_up_date: date || "" }, "Follow-up date saved");

  const openEdit = (l) => {
    setEditLead(l);
    setEditForm({
      notes: l.notes || "",
      follow_up_date: toInputDate(l.follow_up_date) || "",
      status: l.status || "new",
    });
  };

  const saveEdit = async () => {
    if (!editLead) return;
    setEditBusy(true);
    try {
      await patchLeadRemote(editLead.id, {
        notes: editForm.notes,
        follow_up_date: editForm.follow_up_date || "",
        status: editForm.status,
      }, "Lead updated");
      setEditLead(null);
    } catch { /* toast already fired */ }
    finally { setEditBusy(false); }
  };

  const dateRange = useMemo(() => resolveRange(preset, customStart, customEnd), [preset, customStart, customEnd]);
  const scoped = useMemo(() => leads.filter((l) => isInRange(l.created_at, dateRange)), [leads, dateRange]);

  const filtered = scoped.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (l.name || "").toLowerCase().includes(s) ||
      (l.phone || "").toLowerCase().includes(s) ||
      (l.email || "").toLowerCase().includes(s) ||
      (l.agent_name || "").toLowerCase().includes(s) ||
      (l.notes || "").toLowerCase().includes(s)
    );
  });

  const counts = {
    total: scoped.length,
    qualified: scoped.filter((l) => l.status === "qualified").length,
    won: scoped.filter((l) => l.status === "won").length,
    follow_up: scoped.filter((l) => l.status === "follow_up").length,
  };

  const exportRows = filtered.map((l) => ({
    name: l.name || "",
    email: l.email || "",
    phone: l.phone || "",
    status: l.status || "",
    agent: l.agent_name || "",
    follow_up_date: l.follow_up_date || "",
    notes: l.notes || "",
    summary: l.summary || "",
    captured_at: l.created_at || "",
  }));

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">CRM</p>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Leads Captured</h1>
          <p className="text-sm text-muted-foreground mt-1">Prospects auto-extracted from voice agent conversations.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangeFilter
            preset={preset}
            onPresetChange={setPreset}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }}
            testId="leads-date-range"
          />
          <ExportButton rows={exportRows} filename="callswiper-leads" testId="leads-export-btn" />
          <Button variant="outline" size="sm" onClick={load} data-testid="leads-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${leadsLoading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="All leads" value={counts.total} />
        <KpiCard icon={CheckCircle2} label="Qualified" value={counts.qualified} tone="emerald" />
        <KpiCard icon={Trophy} label="Won" value={counts.won} tone="teal" />
        <KpiCard icon={Clock} label="Follow-up" value={counts.follow_up} tone="amber" />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="pl-8 h-9" data-testid="leads-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="leads-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground ml-auto">{filtered.length} of {scoped.length}</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider">Lead</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Contact</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Agent</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Captured</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Follow-up</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Notes</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!leadsFetchedOnce && leadsLoading && <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
              {leadsFetchedOnce && filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">No leads match your filters.</TableCell></TableRow>}
              {filtered.map((l) => (
                <TableRow key={l.id} className="border-border hover:bg-muted/40" data-testid={`lead-row-${l.id}`}>
                  <TableCell>
                    <div className="font-semibold text-sm">{l.name || "Unknown"}</div>
                    {l.email && <div className="text-xs text-muted-foreground font-mono">{l.email}</div>}
                  </TableCell>
                  <TableCell className="text-sm font-mono">{l.phone || "—"}</TableCell>
                  <TableCell className="text-sm">{l.agent_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(l.created_at)}</TableCell>
                  <TableCell>
                    <Input
                      type="date"
                      value={toInputDate(l.follow_up_date) || ""}
                      onChange={(e) => updateFollowUp(l.id, e.target.value)}
                      data-testid={`lead-followup-input-${l.id}`}
                      className="h-8 w-36 text-xs"
                    />
                  </TableCell>
                  <TableCell className="max-w-[220px]">
                    {l.notes ? (
                      <p className="text-xs text-muted-foreground line-clamp-2" title={l.notes}>{l.notes}</p>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid={`lead-status-select-${l.id}`}>
                        <SelectValue>
                          <LeadStatusBadge status={l.status} />
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent align="end">
                        {LEAD_STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openEdit(l)} data-testid={`lead-edit-btn-${l.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!editLead} onOpenChange={(o) => !o && setEditLead(null)}>
        <DialogContent data-testid="lead-edit-modal">
          <DialogHeader>
            <DialogTitle>Edit lead</DialogTitle>
            <DialogDescription>
              {editLead && <>Update notes, follow-up date and status for <span className="font-semibold">{editLead.name || "Unknown"}</span>.</>}
            </DialogDescription>
          </DialogHeader>
          {editLead && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                <Textarea
                  rows={4}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Call outcome, next steps, objections…"
                  data-testid="lead-notes-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Follow-up date</Label>
                  <Input
                    type="date"
                    value={editForm.follow_up_date}
                    onChange={(e) => setEditForm({ ...editForm, follow_up_date: e.target.value })}
                    data-testid="lead-followup-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger data-testid="lead-edit-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditLead(null)}>Cancel</Button>
                <Button onClick={saveEdit} disabled={editBusy} data-testid="lead-edit-save-btn">Save</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "primary" }) {
  const toneCls = {
    primary: "bg-primary/10 border-primary/25 text-primary",
    emerald: "bg-emerald-500/10 border-emerald-500/25 text-emerald-500",
    teal: "bg-teal-500/10 border-teal-500/25 text-teal-500",
    amber: "bg-amber-500/10 border-amber-500/25 text-amber-500",
    rose: "bg-rose-500/10 border-rose-500/25 text-rose-500",
  }[tone];
  return (
    <Card className="p-4 flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold tracking-tight mt-1">{value}</div>
      </div>
      <div className={`w-8 h-8 flex items-center justify-center rounded-md border ${toneCls}`}>
        <Icon className="w-4 h-4" />
      </div>
    </Card>
  );
}
